/**
 * Shared Matcher Item types
 *
 * Used by both MATCHER and PAIR_MATCHER widgets.
 * DisplayInlineNode — the set of inline nodes allowed inside a matcher card (display-only).
 * MatcherItem       — a single card with an id and inline content.
 */

import { z } from 'zod';
import {
  FreeTextInlineSchema,
  SpaceInlineSchema,
  HighlightedTextInlineSchema,
  ColorTextInlineSchema,
  BoldTextInlineSchema,
  FractionInlineSchema,
  ImageInlineSchema,
  VisualFractionInlineSchema,
} from './inline';
import { AST_TYPES } from '../constants';

/** Fraction restricted to display mode — both numerator and denominator required. */
const DisplayFractionInlineSchema = FractionInlineSchema.refine(
  f => f.numerator !== undefined && f.denominator !== undefined,
  { message: 'FRACTION inside MATCHER ITEM must be in display mode (both numerator and denominator required)' },
);

/**
 * VisualFraction restricted to display mode — answer must be empty.
 * Interactive fractions (answer ≠ []) are not allowed inside a Matcher card.
 */
const DisplayVisualFractionInlineSchema = VisualFractionInlineSchema.refine(
  vf => vf.answer.length === 0,
  { message: 'VISUAL_FRACTION inside MATCHER ITEM must be display-only (answer must be empty: answer=[])' },
);

export const DisplayInlineNodeSchema = z.union([
  FreeTextInlineSchema,
  SpaceInlineSchema,
  HighlightedTextInlineSchema,
  ColorTextInlineSchema,
  BoldTextInlineSchema,
  DisplayFractionInlineSchema,
  ImageInlineSchema,
  DisplayVisualFractionInlineSchema,
]);

export type DisplayInlineNode = z.infer<typeof DisplayInlineNodeSchema>;

export const MatcherItemSchema = z.object({
  type: z.literal(AST_TYPES.MATCHER_ITEM),
  itemId: z
    .string()
    .min(1)
    .regex(/^[^|]+$/, { message: "itemId cannot contain '|' (reserved for validation encoding)" }),
  inlines: z.array(DisplayInlineNodeSchema),
});

export type MatcherItem = z.infer<typeof MatcherItemSchema>;

export function createMatcherItem(itemId: string, inlines: DisplayInlineNode[]): MatcherItem {
  return MatcherItemSchema.parse({ type: AST_TYPES.MATCHER_ITEM, itemId, inlines });
}

export function isMatcherItem(value: unknown): value is MatcherItem {
  return MatcherItemSchema.safeParse(value).success;
}
